/**
 * Canonical model: every connector maps to and from these shapes, so N systems
 * need N×2 mappings instead of N×N.
 */

export const ENTITY_TYPES = ["contact", "product", "order", "redirect", "event"] as const;
export type EntityType = (typeof ENTITY_TYPES)[number];

export type ConsentStatus =
  | "subscribed"
  | "unsubscribed"
  | "suppressed" // bounced / cleaned / spam complaint / manually suppressed
  | "never_subscribed"
  | "pending"; // double opt-in not confirmed

export interface ChannelConsent {
  status: ConsentStatus;
  /** ISO timestamp of the original consent (or opt-out) event, when the source has it. */
  at?: string;
  /** Free text: "double_opt_in", "checkout", "import:mailchimp", etc. */
  method?: string;
  /** Why a record is suppressed: "bounced", "spam_complaint", "manual", "cleaned". */
  reason?: string;
}

export interface Consent {
  email?: ChannelConsent;
  sms?: ChannelConsent;
}

/** A single record in canonical form. `key` is the match key used for upserts (email, SKU/handle, order number…). */
export interface CanonicalRecord {
  entity: EntityType;
  key: string;
  fields: Record<string, unknown>;
  consent?: Consent;
  /** Lists/segments/tags the record belongs to in the source. */
  lists?: string[];
  /** Target-side identifier, when known (filled by fetchExisting / write). */
  externalId?: string;
}

/** Well-known canonical fields per entity. Anything else goes under `custom.*`. */
export const CANONICAL_FIELDS: Record<EntityType, readonly string[]> = {
  contact: [
    "email",
    "phone",
    "first_name",
    "last_name",
    "organization",
    "title",
    "address1",
    "address2",
    "city",
    "region",
    "zip",
    "country",
    "locale",
    "timezone",
    "tags",
    "notes",
    "created_at",
  ],
  product: [
    "handle",
    "sku",
    "title",
    "description_html",
    "vendor",
    "product_type",
    "tags",
    "status",
    "price",
    "compare_at_price",
    "currency",
    "inventory_quantity",
    "weight",
    "weight_unit",
    "barcode",
    "image_urls",
    "seo_title",
    "seo_description",
    "option1_name",
    "option1_value",
    "option2_name",
    "option2_value",
    "option3_name",
    "option3_value",
    "parent_handle",
  ],
  order: [
    "order_number",
    "email",
    "created_at",
    "currency",
    "total_price",
    "subtotal_price",
    "total_tax",
    "shipping_price",
    "financial_status",
    "fulfillment_status",
    "line_items",
    "shipping_address",
    "billing_address",
    "tags",
    "note",
  ],
  redirect: ["path", "target"],
  event: ["email", "metric", "time", "value", "properties", "unique_id"],
};

export function isCustomField(field: string): boolean {
  return field.startsWith("custom.");
}

export function normalizeEmail(raw: unknown): string | undefined {
  if (typeof raw !== "string") return undefined;
  const e = raw.trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e) ? e : undefined;
}
